/*
  t4m minicart v2 — behaviour layer for the existing cart drawer.
  - swipe-down to close the mobile bottom sheet (uses cart-drawer's own close())
  - mobile dock ("Ver sacola · N itens  R$ X") kept in sync with the cart;
    it tucks away while scrolling down, comes back on scroll up, and stays
    up for a few seconds after a product is added
  No cart logic lives here: all cart mutations stay in cart.js / cart-drawer.js.
*/
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 719.98px)';
  var DRAG_ZONE = '.t4m-minicart__handle, .drawer__header';
  var CLOSE_DISTANCE = 90;
  var FLICK_VELOCITY = 0.5; // px per ms
  var FLICK_MIN_DISTANCE = 20;
  var REFRESH_DEBOUNCE = 300;
  var DOCK_ADD_HOLD = 4000; // ms the dock stays up after an add
  var DOCK_SCROLL_DELTA = 8; // px of scroll before the dock reacts
  var DOCK_TOP_ZONE = 120; // px near the top where the dock never tucks

  function init() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var mobile = window.matchMedia(MOBILE_QUERY);
    initSwipe(drawer, mobile);
    initDock(drawer, mobile);
  }

  /* ---------- Swipe to close ---------- */

  function initSwipe(drawer, mobile) {
    var drag = null;

    function reset(sheet) {
      sheet.style.transition = '';
      sheet.style.transform = '';
    }

    drawer.addEventListener('pointerdown', function (event) {
      if (!mobile.matches || !drawer.classList.contains('active')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      var zone = event.target.closest(DRAG_ZONE);
      if (!zone || event.target.closest('button, a, input')) return;
      var sheet = drawer.querySelector('.drawer__inner');
      if (!sheet) return;
      drag = { id: event.pointerId, startY: event.clientY, startT: performance.now(), dy: 0, sheet: sheet };
      try { zone.setPointerCapture(event.pointerId); } catch (e) { /* capture is optional */ }
    });

    drawer.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      drag.dy = Math.max(0, event.clientY - drag.startY);
      drag.sheet.style.transition = 'none';
      drag.sheet.style.transform = 'translateY(' + drag.dy + 'px)';
    });

    function end(event) {
      if (!drag || event.pointerId !== drag.id) return;
      var elapsed = Math.max(performance.now() - drag.startT, 1);
      var velocity = drag.dy / elapsed;
      var shouldClose =
        event.type === 'pointerup' &&
        (drag.dy > CLOSE_DISTANCE || (velocity > FLICK_VELOCITY && drag.dy > FLICK_MIN_DISTANCE));
      reset(drag.sheet);
      drag = null;
      if (shouldClose && typeof drawer.close === 'function') drawer.close();
    }

    drawer.addEventListener('pointerup', end);
    drawer.addEventListener('pointercancel', end);
  }

  /* ---------- Mobile dock ---------- */

  function formatMoney(cents) {
    var format = window.theme && window.theme.settings && window.theme.settings.money_with_currency_format;
    if (typeof window.formatMoney === 'function' && format) {
      try { return window.formatMoney(cents, format); } catch (e) { /* fall through */ }
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
  }

  function initDock(drawer, mobile) {
    var dock = document.getElementById('T4mMinicartDock');
    if (!dock) return;
    var countEl = dock.querySelector('[data-t4m-dock-count]');
    var totalEl = dock.querySelector('[data-t4m-dock-total]');
    var refreshTimer = null;
    var requestId = 0;
    // Baseline from the Liquid-rendered cart, so the first add already counts as an add.
    var initialCount = parseInt(dock.dataset.itemCount, 10);
    var lastCount = isNaN(initialCount) ? null : initialCount;
    var holdUntil = 0;

    dock.setAttribute('role', 'button');
    dock.setAttribute('aria-haspopup', 'dialog');

    dock.addEventListener('click', function (event) {
      if (typeof drawer.open !== 'function') return;
      event.preventDefault();
      drawer.open(dock);
    });
    dock.addEventListener('keydown', function (event) {
      if (event.key === ' ' && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(dock);
      }
    });

    function syncBodyClass() {
      var visible = !dock.hidden && window.getComputedStyle(dock).display !== 'none';
      document.body.classList.toggle('has-dock', visible);
    }

    function render(cart) {
      if (!cart || typeof cart.item_count !== 'number') return;
      var count = cart.item_count;
      var template = count === 1 ? dock.dataset.labelOne : dock.dataset.labelOther;
      if (countEl && template) countEl.textContent = template.replace('[n]', String(count));
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
      dock.hidden = count === 0;
      if (lastCount !== null && count > lastCount) {
        holdUntil = Date.now() + DOCK_ADD_HOLD;
        dock.classList.remove('is-tucked');
      }
      lastCount = count;
      syncBodyClass();
    }

    var lastScrollY = window.scrollY;
    var scrollTicking = false;

    function onScroll() {
      scrollTicking = false;
      var y = window.scrollY;
      var delta = y - lastScrollY;
      if (Math.abs(delta) < DOCK_SCROLL_DELTA) return;
      lastScrollY = y;
      if (y < DOCK_TOP_ZONE || delta < 0) {
        dock.classList.remove('is-tucked');
      } else if (Date.now() > holdUntil) {
        dock.classList.add('is-tucked');
      }
    }

    window.addEventListener('scroll', function () {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(onScroll);
    }, { passive: true });

    function refresh() {
      var current = ++requestId;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('cart.js ' + response.status);
          return response.json();
        })
        .then(function (cart) {
          if (current === requestId) render(cart);
        })
        .catch(function (error) {
          console.warn('[t4m-minicart] dock refresh failed', error);
        });
    }

    function scheduleRefresh(delay) {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refresh, delay || REFRESH_DEBOUNCE);
    }

    // cart.js fires 'cartUpdated' before its request resolves, so wait a bit longer.
    document.addEventListener('cartUpdated', function () { scheduleRefresh(700); });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.cartData && typeof event.cartData.item_count === 'number' && typeof event.cartData.total_price === 'number') {
          clearTimeout(refreshTimer);
          render(event.cartData);
        } else {
          scheduleRefresh();
        }
      });
    }

    // Drawer re-renders (add to cart, coupon reload, quantity change) replace element nodes.
    var cartRoot = document.getElementById('CartDrawer');
    if (cartRoot) {
      new MutationObserver(function (mutations) {
        var hasElementChange = mutations.some(function (mutation) {
          return Array.prototype.some.call(mutation.addedNodes, function (node) { return node.nodeType === 1; });
        });
        if (hasElementChange) scheduleRefresh();
      }).observe(cartRoot, { childList: true, subtree: true });
    }

    // Visibility depends on drawer state, other modals and viewport width.
    var stateObserver = new MutationObserver(syncBodyClass);
    stateObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    stateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    var stickyBar = document.querySelector('.product-add-to-cart-sticky');
    if (stickyBar) stateObserver.observe(stickyBar, { attributes: true, attributeFilter: ['class'] });
    if (typeof mobile.addEventListener === 'function') {
      mobile.addEventListener('change', syncBodyClass);
    } else if (typeof mobile.addListener === 'function') {
      mobile.addListener(syncBodyClass);
    }

    syncBodyClass();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
